import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { runCheck, fetchUsage, extractError } from "../lib/api";
import { GuestTracker } from "../components/GuestTracker";
import { ResultCard } from "../components/ResultCard";
import {
  MessageSquareText, Link2, Camera, FileText, FileType2,
  UploadCloud, X, AlertCircle, Loader2, Sparkles, UserCheck,
} from "lucide-react";

const TABS = [
  { key: "text_message", icon: MessageSquareText, testid: "check-input-tab-text" },
  { key: "url", icon: Link2, testid: "check-input-tab-url" },
  { key: "image_screenshot", icon: Camera, testid: "check-input-tab-screenshot" },
  { key: "image_letter", icon: FileText, testid: "check-input-tab-letter" },
  { key: "document_pdf", icon: FileType2, testid: "check-input-tab-document" },
];

const isImage = (k) => k === "image_screenshot" || k === "image_letter";
const isFile = (k) => isImage(k) || k === "document_pdf";

export default function Check() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("text_message");
  const [mode, setMode] = useState("check");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [progress, setProgress] = useState(0);
  const progressTimer = useRef(null);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);

  const stopProgress = () => {
    if (progressTimer.current) { clearInterval(progressTimer.current); progressTimer.current = null; }
  };
  const startProgress = (heavy) => {
    stopProgress();
    setProgress(1);
    const cap = 95;
    const factor = heavy ? 0.045 : 0.09;
    progressTimer.current = setInterval(() => {
      setProgress((p) => (p >= cap ? p : Math.min(cap, p + Math.max(0.4, (cap - p) * factor))));
    }, 180);
  };
  useEffect(() => () => stopProgress(), []);

  const progressStage = () => {
    if (progress >= 100) return t("check.progress.done");
    if (isFile(tab)) {
      if (progress < 35) return t("check.progress.uploading");
      if (progress < 65) return t("check.progress.reading");
      return t("check.progress.analyzing");
    }
    if (progress < 60) return t("check.progress.analyzing");
    return t("check.progress.patterns");
  };

  const loadUsage = useCallback(async () => {
    try { setUsage(await fetchUsage()); } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  const resetInputs = () => { setText(""); setUrl(""); setFiles([]); setError(null); };
  const expertMode = new URLSearchParams(location.search).get("expert") === "1";

  const switchTab = (k) => { setTab(k); resetInputs(); setResult(null); };

  const scrollToResult = () => {
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const showExample = (c) => {
    setError(null);
    setResult({
      analysis_id: "demo", status: "completed", analysis_status: "completed", is_demo: true,
      risk_score: c.score, risk_level: c.level, summary: c.summary,
      signals: c.signals, recommended_action: c.advice, do_not_do: c.doNotDo,
      transparency_disclaimer: t("home.demo.note"), input_type: c.type, preview: c.preview,
    });
    scrollToResult();
  };

  // open the examples tab when navigated with #demo
  useEffect(() => {
    if (location.hash === "#demo") setMode("examples");
  }, [location.hash]);

  const onFiles = (fileList) => {
    const arr = Array.from(fileList);
    if (tab === "document_pdf") setFiles(arr.slice(0, 1));
    else setFiles((prev) => [...prev, ...arr].slice(0, 5));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const canSubmit = () => {
    if (loading) return false;
    if (tab === "url") return url.trim().length > 0;
    if (tab === "text_message" || tab === "email_text") return text.trim().length > 0;
    return files.length > 0;
  };

  const submit = async () => {
    setError(null); setResult(null); setLoading(true); setProgress(0); startProgress(isFile(tab));
    try {
      const payload = { inputType: tab, lang };
      if (tab === "url") payload.url = url.trim();
      else if (tab === "text_message") payload.text = text;
      else payload.files = files;
      const data = await runCheck(payload);
      stopProgress(); setProgress(100);
      await new Promise((r) => setTimeout(r, 350));
      setResult(data);
      if (data.usage) setUsage(data.usage);
      else loadUsage();
      setLoading(false);
      scrollToResult();
    } catch (err) {
      stopProgress(); setProgress(0);
      setError(extractError(err, t("check.genericError")));
      setLoading(false);
    }
  };

  const outOfChecks = usage && usage.remaining <= 0;
  const exampleCases = t("check.exampleCases");

  return (
    <div className="tw-grid-bg min-h-screen pt-28 pb-24 sm:pt-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("check.title")}</h1>
            <p className="mt-2 text-base text-slate-500">{t("check.subtitle")}</p>
          </div>
          <GuestTracker usage={user ? null : usage} />
        </div>

        {/* Mode toggle */}
        <div className="mt-8 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="check-mode-toggle">
          <button
            data-testid="check-mode-check"
            onClick={() => setMode("check")}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${mode === "check" ? "bg-[#1E3A8A] text-white" : "text-slate-500 hover:text-slate-900"}`}
          >
            {t("check.modes.check")}
          </button>
          <button
            data-testid="check-mode-examples"
            onClick={() => { setMode("examples"); setResult(null); }}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${mode === "examples" ? "bg-[#1E3A8A] text-white" : "text-slate-500 hover:text-slate-900"}`}
          >
            {t("check.modes.examples")}
          </button>
        </div>

        {/* Expert Review intent hint */}
        {expertMode && mode === "check" && !result && (
          <div data-testid="check-expert-hint" className="mt-6 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-5">
            <UserCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
            <p className="text-sm text-sky-100">{t("check.expertHint")}</p>
          </div>
        )}

        {/* Out of checks banner */}
        {outOfChecks && (
          <div data-testid="check-out-of-checks" className="mt-6 flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-amber-800">{t("check.guest.none")}</p>
            <Link to="/pricing" className="rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-400">
              {t("check.guest.cta")}
            </Link>
          </div>
        )}

        {mode === "check" && (
        <>
        {/* Tabs */}
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {TABS.map(({ key, icon: Icon, testid }) => (
            <button
              key={key}
              data-testid={testid}
              onClick={() => switchTab(key)}
              className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center transition-all ${
                tab === key
                  ? "border-sky-500/50 bg-blue-50 text-slate-900"
                  : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-50"
              }`}
            >
              <Icon className={`h-5 w-5 ${tab === key ? "text-blue-700" : ""}`} />
              <span className="text-xs font-medium leading-tight">{t(`check.tabs.${key}`)}</span>
            </button>
          ))}
        </div>

        {/* Input panel */}
        <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
          {tab === "text_message" && (
            <textarea
              data-testid="check-text-input-field"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("check.textPlaceholder")}
              rows={7}
              maxLength={20000}
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-900 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          )}

          {tab === "url" && (
            <div>
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 focus-within:border-sky-500/50 focus-within:ring-2 focus-within:ring-blue-200">
                <Link2 className="h-5 w-5 shrink-0 text-slate-500" />
                <input
                  data-testid="check-url-input-field"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("check.urlPlaceholder")}
                  className="w-full bg-transparent py-4 text-sm text-slate-900 placeholder:text-slate-600 focus:outline-none"
                />
              </div>
            </div>
          )}

          {isFile(tab) && (
            <div>
              <div
                data-testid="check-file-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
                className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
                  dragging ? "border-sky-500/60 bg-blue-50" : "border-slate-200 bg-slate-100 hover:border-blue-300 hover:bg-slate-50"
                }`}
              >
                <UploadCloud className="h-9 w-9 text-slate-500" />
                <p className="mt-3 text-sm font-medium text-slate-600">{t("check.uploadTitle")}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {tab === "document_pdf" ? t("check.uploadHintPdf") : t("check.uploadHintImage")}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  data-testid="check-file-input"
                  accept={tab === "document_pdf" ? "application/pdf" : "image/*"}
                  multiple={isImage(tab)}
                  className="hidden"
                  onChange={(e) => onFiles(e.target.files)}
                />
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("check.selectedFiles")}</p>
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <FileType2 className="h-4 w-4 shrink-0 text-blue-700" />
                        <span className="truncate text-sm text-slate-600">{f.name}</span>
                        <span className="shrink-0 text-xs text-slate-600">{(f.size / 1024).toFixed(0)} KB</span>
                      </div>
                      <button onClick={() => removeFile(i)} className="ml-2 text-slate-500 hover:text-rose-400" aria-label={t("check.remove")}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div data-testid="check-error" className="mt-4 flex items-start gap-2.5 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-400" />
              <div>
                <p className="text-sm font-semibold text-rose-300">{t("check.errorTitle")}</p>
                <p className="text-sm text-slate-600">{error}</p>
              </div>
            </div>
          )}

          <button
            data-testid="check-submit-button"
            onClick={submit}
            disabled={!canSubmit() || outOfChecks}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> {t("check.analyzing")}</>
            ) : (
              <><Sparkles className="h-5 w-5" /> {t("check.submit")}</>
            )}
          </button>
        </div>
        </>
        )}

        {mode === "examples" && (
          <div className="mt-6" data-testid="examples-panel">
            <p className="text-sm text-slate-500">{t("check.examplesSubtitle")}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {exampleCases.map((c, i) => (
                <div key={c.key} data-testid={`example-case-${i}`} className="flex flex-col rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500">
                      {t(`check.tabs.${c.type}`)}
                    </span>
                    <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                      {t("check.exampleLabel")}
                    </span>
                  </div>
                  <h3 className="mt-3 text-base font-semibold text-slate-900">{c.label}</h3>
                  <p className="mt-1.5 flex-1 text-sm leading-relaxed text-slate-500">"{c.preview}"</p>
                  <button
                    data-testid={`example-view-${i}`}
                    onClick={() => showExample(c)}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-blue-300 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-[#1D4ED8]/20"
                  >
                    {t("check.viewAnalysis")}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress + loading skeleton */}
        {loading && !result && (
          <div className="mt-8 space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="check-progress">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Loader2 className="h-4 w-4 animate-spin text-[#1E3A8A]" /> {progressStage()}
                </div>
                <span className="text-sm font-bold tabular-nums text-[#1E3A8A]" data-testid="check-progress-pct">{Math.round(progress)}%</span>
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#1D4ED8] transition-[width] duration-200 ease-out"
                  style={{ width: `${progress}%` }}
                  data-testid="check-progress-bar"
                />
              </div>
              <p className="mt-2 text-xs text-slate-400">{t("check.progress.hint")}</p>
            </div>

            <div className="animate-pulse rounded-3xl border border-slate-200 bg-slate-50 p-8">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-slate-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 rounded bg-slate-100" />
                  <div className="h-3 w-1/2 rounded bg-slate-50" />
                </div>
                <div className="h-24 w-24 rounded-full bg-slate-100" />
              </div>
              <div className="mt-6 space-y-3">
                <div className="h-3 w-full rounded bg-slate-50" />
                <div className="h-3 w-5/6 rounded bg-slate-50" />
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div ref={resultRef} className="mt-8 scroll-mt-28">
            <ResultCard result={result} onCheckAnother={() => { setResult(null); resetInputs(); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          </div>
        )}
      </div>
    </div>
  );
}
